'use client';

import { memo } from 'react';
import {
  Box,
  Typography,
  Stack,
  Card,
  CardContent,
  Grid,
  IconButton,
  Skeleton,
  alpha,
} from '@mui/material';
import { MyLocation, Refresh, Timer, Navigation, AccessTime } from '@mui/icons-material';
import { formatETA } from '../../../hooks/useETACalculation';
import { useTranslation } from '@/kit/useTranslation';

/**
 * Live tracking card with ETA, distance, and rider location
 */
const LiveTrackingCard = memo(
  ({
    statusColor,
    durationText,
    distanceText,
    eta,
    etaLoading,
    riderLocation,
    riderName,
    onRefresh,
  }) => {
    const { t } = useTranslation();
    return (
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          border: '1px solid',
          borderColor: alpha(statusColor, 0.3),
          bgcolor: alpha(statusColor, 0.05),
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <MyLocation sx={{ color: statusColor, fontSize: 18 }} />
              <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#1f2937' }}>
                {t('bookingDetails.liveTracking')}
              </Typography>
            </Stack>
            <IconButton
              size="small"
              onClick={onRefresh}
              disabled={etaLoading}
              sx={{ color: statusColor, p: 0.5 }}
            >
              <Refresh sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack>

          <Grid container spacing={2}>
            {/* ETA */}
            <Grid item xs={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Timer sx={{ color: statusColor, fontSize: 28, mb: 0.5 }} />
                {etaLoading ? (
                  <Skeleton width={60} height={24} sx={{ mx: 'auto' }} />
                ) : (
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: '1.1rem',
                      color: '#1f2937',
                    }}
                  >
                    {durationText || t('bookingDetails.calculating')}
                  </Typography>
                )}
                <Typography sx={{ fontSize: '0.7rem', color: '#6b7280', mt: 0.25 }}>
                  {t('bookingDetails.estTime')}
                </Typography>
              </Box>
            </Grid>

            {/* Distance */}
            <Grid item xs={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Navigation sx={{ color: '#10b981', fontSize: 28, mb: 0.5 }} />
                {etaLoading ? (
                  <Skeleton width={60} height={24} sx={{ mx: 'auto' }} />
                ) : (
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: '1.1rem',
                      color: '#1f2937',
                    }}
                  >
                    {distanceText || '—'}
                  </Typography>
                )}
                <Typography sx={{ fontSize: '0.7rem', color: '#6b7280', mt: 0.25 }}>
                  {t('bookingDetails.distance')}
                </Typography>
              </Box>
            </Grid>

            {/* ETA Time */}
            <Grid item xs={4}>
              <Box sx={{ textAlign: 'center' }}>
                <AccessTime sx={{ color: '#f59e0b', fontSize: 28, mb: 0.5 }} />
                {etaLoading ? (
                  <Skeleton width={60} height={24} sx={{ mx: 'auto' }} />
                ) : (
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: '1.1rem',
                      color: '#1f2937',
                    }}
                  >
                    {eta ? formatETA(eta) : '—'}
                  </Typography>
                )}
                <Typography sx={{ fontSize: '0.7rem', color: '#6b7280', mt: 0.25 }}>
                  {t('bookingDetails.arrivesBy')}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Rider's last known location */}
          {riderLocation && (
            <Box
              sx={{
                mt: 2,
                pt: 1.5,
                borderTop: '1px dashed',
                borderColor: alpha(statusColor, 0.2),
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: '#10b981',
                    animation: 'pulse 2s infinite',
                    '@keyframes pulse': {
                      '0%': { opacity: 1, transform: 'scale(1)' },
                      '50%': { opacity: 0.5, transform: 'scale(1.2)' },
                      '100%': { opacity: 1, transform: 'scale(1)' },
                    },
                  }}
                />
                <Typography sx={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  {t('bookingDetails.riderLocationUpdated')} •{' '}
                  {t('bookingDetails.riderOnTheWay', { name: riderName || t('parcel.rider') })}
                </Typography>
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  }
);

LiveTrackingCard.displayName = 'LiveTrackingCard';

export default LiveTrackingCard;
